import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { ZyntraLogo } from "@/components/ZyntraLogo";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { verifyTurnstile } from "@/lib/turnstile.functions";
import { useServerFn } from "@tanstack/react-start";
import type { TurnstileInstance } from "@marsidev/react-turnstile";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  head: () => ({
    meta: [
      { title: "Iniciar Sesión — ZYNTRA" },
      { name: "description", content: "Ingresá a tu cuenta ZYNTRA para comprar y gestionar tus entradas." },
    ],
  }),
});

function LoginPage() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);
  const verifyTurnstileFn = useServerFn(verifyTurnstile);

  function resetTurnstile() {
    setTurnstileToken("");
    turnstileRef.current?.reset();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!turnstileToken) {
      setError("Esperá a que termine la verificación anti-bot.");
      return;
    }

    setLoading(true);
    try {
      const verify = await verifyTurnstileFn({ data: { token: turnstileToken } });
      if (!verify.success) {
        setError(verify.error || "No pudimos verificar que sos humano");
        resetTurnstile();
        return;
      }

      if (isRegister) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: { full_name: name },
          },
        });
        if (error) throw error;
        setSuccess("¡Registro exitoso! Revisá tu email para confirmar tu cuenta.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: "/" });
      }
    } catch (err: any) {
      setError(err.message || "Ocurrió un error");
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleLogin() {
    setError("");
    if (!turnstileToken) {
      setError("Esperá a que termine la verificación anti-bot antes de continuar con Google.");
      return;
    }
    setLoading(true);
    try {
      const verify = await verifyTurnstileFn({ data: { token: turnstileToken } });
      if (!verify.success) {
        setError(verify.error || "No pudimos verificar que sos humano");
        resetTurnstile();
        setLoading(false);
        return;
      }
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        setError(result.error.message || "Error al iniciar con Google");
        setLoading(false);
        return;
      }
      if (result.redirected) return;
      navigate({ to: "/" });
    } catch (err: any) {
      setError(err.message || "Error al iniciar con Google");
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 text-center">
          <ZyntraLogo className="mx-auto mb-4 h-20 w-auto" />
          <h1 className="text-2xl font-black text-foreground">
            {isRegister ? "Crear cuenta" : "Bienvenido"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isRegister ? "Registrate para comprar entradas" : "Ingresá a tu cuenta de Zyntra Tickets"}
          </p>
        </div>

        {/* Google button */}
        <button
          onClick={handleGoogleLogin}
          disabled={loading || !turnstileToken}
          title={!turnstileToken ? "Completá la verificación anti-bot debajo" : undefined}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card py-3 text-sm font-semibold text-card-foreground transition-colors hover:bg-accent disabled:opacity-50"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continuar con Google
        </button>

        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">o con email</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {isRegister && (
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Nombre completo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={isRegister}
                className="w-full rounded-xl border border-border bg-input py-3 pl-10 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-xl border border-border bg-input py-3 pl-10 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full rounded-xl border border-border bg-input py-3 pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {error && (
            <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
          )}
          {success && (
            <p className="rounded-lg bg-success/10 px-3 py-2 text-xs text-success">{success}</p>
          )}

          <TurnstileWidget
            ref={turnstileRef}
            onSuccess={setTurnstileToken}
            onError={() => setTurnstileToken("")}
            onExpire={() => setTurnstileToken("")}
          />

          <button
            type="submit"
            disabled={loading || !turnstileToken}
            className="w-full rounded-xl gradient-primary py-3 text-sm font-bold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Cargando..." : isRegister ? "Crear cuenta" : "Ingresar"}
          </button>
        </form>

        <p className="mt-5 text-center text-xs text-muted-foreground">
          {isRegister ? "¿Ya tenés cuenta?" : "¿No tenés cuenta?"}{" "}
          <button
            onClick={() => {
              setIsRegister(!isRegister);
              setError("");
              setSuccess("");
            }}
            className="font-semibold text-primary hover:underline"
          >
            {isRegister ? "Iniciar sesión" : "Registrate"}
          </button>
        </p>
      </div>
    </div>
  );
}
